import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, TrendingUp, Users, Package, Zap, Clock, 
  Calendar, RefreshCw, Download, Lock, Crown, ArrowUpRight,
  ArrowDownRight, Activity, ShoppingCart, Heart, CreditCard,
  Sparkles, Eye, Target, PieChart, Layers, AlertTriangle,
  TrendingDown, Star, Filter, ChevronDown, ChevronUp,
  DollarSign, Percent, Box, Shirt, Loader2
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, Radar
} from 'recharts';
import { analyticsAPI, subscriptionAPI } from '../../api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// Colors
const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
const MOMENT_COLORS = {
  creative_workday: '#3b82f6',
  unwind: '#10b981', 
  after_hours: '#8b5cf6',
  escape: '#f59e0b',
  gentle_celebration: '#ec4899'
};

// Subscription tier order
const TIER_ORDER = { starter: 0, growth: 1, professional: 2, enterprise: 3 };

// ==================== COMPONENTS ====================

// Blurred Section Overlay for locked content
const LockedOverlay = ({ requiredPlan, currentPlan, featureName }) => {
  const navigate = useNavigate();
  
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm z-10 rounded-xl">
      <div className="text-center p-6 max-w-xs">
        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Lock className="w-6 h-6 text-slate-400" />
        </div>
        <p className="text-slate-700 font-semibold mb-1">{featureName}</p>
        <p className="text-slate-500 text-sm mb-4">
          Upgrade to <span className="font-semibold capitalize">{requiredPlan}</span> to unlock
        </p>
        <button
          onClick={() => navigate('/partners/subscription')}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
        >
          <Crown className="w-4 h-4" />
          Upgrade Now
        </button>
      </div>
    </div>
  );
};

// Section wrapper with optional blur
const AnalyticsSection = ({ 
  title, 
  icon: Icon, 
  children, 
  locked = false, 
  requiredPlan = 'growth',
  currentPlan = 'starter',
  featureName,
  className = ''
}) => {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden relative ${className}`}>
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Icon className="w-5 h-5 text-blue-600" />
          {title}
        </h3>
        {locked && (
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
            <Lock className="w-3 h-3" />
            {requiredPlan}+
          </span>
        )}
      </div>
      <div className={`p-4 ${locked ? 'filter blur-[2px] select-none pointer-events-none' : ''}`}>
        {children}
      </div>
      {locked && (
        <LockedOverlay 
          requiredPlan={requiredPlan} 
          currentPlan={currentPlan}
          featureName={featureName || title}
        />
      )}
    </div>
  );
};

// KPI Card
const KPICard = ({ label, value, icon: Icon, color, trend, subtitle, locked = false }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
    slate: 'bg-slate-100 text-slate-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600'
  };

  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-4 relative ${locked ? 'overflow-hidden' : ''}`}>
      {locked && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
          <Lock className="w-5 h-5 text-slate-400" />
        </div>
      )}
      <div className={locked ? 'filter blur-sm' : ''}>
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 text-xs font-medium ${
              trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-slate-500'
            }`}>
              {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : trend < 0 ? <ArrowDownRight className="w-3 h-3" /> : null}
              {trend !== 0 && `${Math.abs(trend)}%`}
            </div>
          )}
        </div>
        <div className="text-2xl font-bold text-slate-900">{locked ? '---' : value}</div>
        <div className="text-xs text-slate-500 mt-1">{subtitle || label}</div>
      </div>
    </div>
  );
};

// Conversion Funnel
const ConversionFunnel = ({ data, locked = false }) => {
  if (!data || data.total_sessions === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No conversion data yet</p>
      </div>
    );
  }

  const stages = [
    { name: 'Try-Ons', count: data.total_sessions, rate: 100, color: 'bg-blue-500' },
    { name: 'Liked', count: data.total_likes, rate: data.like_rate || 0, color: 'bg-rose-500' },
    { name: 'Buy Clicked', count: data.total_buy_clicks || 0, rate: data.buy_click_rate || 0, color: 'bg-green-600', highlight: true },
    { name: 'Interested', count: data.total_buy_interest || 0, rate: data.interest_rate || 0, color: 'bg-amber-500' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
        <span>{data.unique_customers || 0} unique customers</span>
        <span className="font-semibold text-green-600">{data.buy_click_rate || 0}% conversion</span>
      </div>
      {stages.map((stage, index) => {
        const widthPercent = Math.max(25, stage.rate);
        return (
          <div key={stage.name} className="relative">
            <div 
              className={`${stage.color} ${stage.highlight ? 'ring-2 ring-green-300' : ''} rounded-lg p-3 text-white transition-all`}
              style={{ width: `${widthPercent}%`, minWidth: '120px' }}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{stage.name}</span>
                <span className="text-lg font-bold">{locked ? '---' : stage.count}</span>
              </div>
              <div className="text-xs opacity-80">{locked ? '--' : stage.rate}%</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// SKU Performance Table
const SKUPerformanceTable = ({ skus, locked = false }) => {
  if (!skus || skus.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No SKU data available</p>
        <p className="text-sm mt-1">Pass sku_id with API calls to track</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left py-3 px-3 font-semibold text-slate-600">SKU</th>
            <th className="text-right py-3 px-3 font-semibold text-slate-600">Try-Ons</th>
            <th className="text-right py-3 px-3 font-semibold text-slate-600">Customers</th>
            <th className="text-right py-3 px-3 font-semibold text-slate-600">Cache %</th>
            <th className="text-right py-3 px-3 font-semibold text-slate-600">Credits</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {skus.slice(0, 10).map((sku, index) => (
            <tr key={sku.sku_id} className="hover:bg-slate-50">
              <td className="py-3 px-3">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-amber-100 text-amber-700' :
                    index === 1 ? 'bg-slate-200 text-slate-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {index + 1}
                  </span>
                  <span className="font-mono text-slate-700 truncate max-w-[120px]">
                    {locked ? '••••••' : sku.sku_id}
                  </span>
                </div>
              </td>
              <td className="py-3 px-3 text-right font-semibold">{locked ? '--' : sku.count}</td>
              <td className="py-3 px-3 text-right text-slate-600">{locked ? '--' : (sku.unique_customers || '-')}</td>
              <td className="py-3 px-3 text-right text-slate-600">{locked ? '--' : `${sku.cached_rate || 0}%`}</td>
              <td className="py-3 px-3 text-right text-slate-600">{locked ? '--' : sku.credits}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Hourly Heatmap
const HourlyHeatmap = ({ data, locked = false }) => {
  if (!data) return null;
  
  const hours = Object.entries(data).map(([hour, count]) => ({ hour, count }));
  const maxCount = Math.max(...hours.map(h => h.count)) || 1;

  return (
    <div>
      <div className="flex gap-0.5 h-24">
        {hours.map(({ hour, count }) => {
          const intensity = count / maxCount;
          const bgColor = intensity > 0.7 ? 'bg-blue-600' : 
                         intensity > 0.4 ? 'bg-blue-400' : 
                         intensity > 0.1 ? 'bg-blue-200' : 'bg-slate-100';
          return (
            <div 
              key={hour}
              className={`flex-1 ${bgColor} rounded-sm cursor-pointer transition-colors hover:opacity-80`}
              title={`${hour}:00 - ${locked ? '--' : count} requests`}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-xs text-slate-400">
        <span>00:00</span>
        <span>06:00</span>
        <span>12:00</span>
        <span>18:00</span>
        <span>24:00</span>
      </div>
      <p className="text-center text-xs text-slate-500 mt-2">Request distribution by hour (UTC)</p>
    </div>
  );
};

// Moments Breakdown
const MomentsBreakdown = ({ data, locked = false }) => {
  const MOMENT_LABELS = {
    creative_workday: 'Workday',
    unwind: 'Unwind',
    after_hours: 'Evening',
    escape: 'Vacation',
    gentle_celebration: 'Celebration'
  };

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No Moments data yet</p>
      </div>
    );
  }

  const moments = Object.entries(data).map(([type, count]) => ({
    type,
    label: MOMENT_LABELS[type] || type,
    count,
    color: MOMENT_COLORS[type] || '#6b7280'
  })).sort((a, b) => b.count - a.count);

  const total = moments.reduce((sum, m) => sum + m.count, 0);

  return (
    <div className="space-y-3">
      {moments.map((moment) => (
        <div key={moment.type} className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: moment.color }} />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-700">{moment.label}</span>
              <span className="text-sm text-slate-600">{locked ? '--' : moment.count}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: locked ? '30%' : `${(moment.count / total) * 100}%`,
                  backgroundColor: moment.color 
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Business Intelligence - Stock Alerts
const StockAlerts = ({ alerts, locked = false }) => {
  if (!alerts || alerts.length === 0) {
    return (
      <div className="text-center py-6 text-slate-400">
        <Box className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No inventory alerts</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {alerts.slice(0, 5).map((alert, i) => (
        <div 
          key={i} 
          className={`p-3 rounded-lg ${
            alert.severity === 'high' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
          }`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 mt-0.5 ${
              alert.severity === 'high' ? 'text-red-500' : 'text-amber-500'
            }`} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-slate-800 truncate">
                {locked ? '••••••' : alert.sku_id}
              </p>
              <p className="text-xs text-slate-600">
                {alert.alert_type === 'high_demand_low_stock' 
                  ? `High demand (${locked ? '--' : alert.tryons} try-ons) • Low stock (${locked ? '--' : alert.stock_remaining} units)`
                  : `Low demand • High stock`
                }
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('starter');
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [exporting, setExporting] = useState(false);
  
  // Data states
  const [usageData, setUsageData] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [publicTryonSummary, setPublicTryonSummary] = useState(null);
  const [inventoryAlerts, setInventoryAlerts] = useState([]);

  // Check tier access
  const getTierLevel = (plan) => TIER_ORDER[plan?.toLowerCase()] || 0;
  const hasAccess = (requiredPlan) => getTierLevel(currentPlan) >= getTierLevel(requiredPlan);
  
  // Tier checks
  const isStarter = currentPlan === 'starter';
  const hasGrowthAccess = hasAccess('growth');
  const hasProfessionalAccess = hasAccess('professional');
  const hasEnterpriseAccess = hasAccess('enterprise');

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // Use unified analytics endpoint that provides tiered data based on plan
      const unifiedRes = await analyticsAPI.getUnified(selectedPeriod).catch((err) => {
        console.error('Unified analytics error:', err);
        return { data: null };
      });
      
      if (unifiedRes.data) {
        // Set plan from response
        setCurrentPlan(unifiedRes.data.plan || 'starter');
        
        // Map unified response to component state
        setUsageData({
          summary: unifiedRes.data.summary || {},
          daily_breakdown: unifiedRes.data.daily_breakdown || [],
          top_skus: unifiedRes.data.top_skus || [],
          top_customers: unifiedRes.data.top_customers || [],
          hourly_distribution: unifiedRes.data.hourly_distribution || {},
          moment_breakdown: unifiedRes.data.moment_breakdown || {}
        });
        
        setCreditData({
          current_balance: unifiedRes.data.current_balance || 0,
          usage_forecast: unifiedRes.data.usage_forecast || {}
        });
        
        // Conversion data for Professional+
        setPublicTryonSummary(unifiedRes.data.conversion || {});
        
        // Inventory alerts for Professional+
        setInventoryAlerts(unifiedRes.data.inventory_alerts || []);
      } else {
        // Fallback to individual API calls if unified fails
        const [subRes] = await Promise.all([
          subscriptionAPI.getCurrent().catch(() => ({ data: { plan: 'starter' } }))
        ]);
        setCurrentPlan((subRes.data.plan || subRes.data.current_plan || 'starter').toLowerCase());
        
        // Set empty data
        setUsageData({ summary: {}, daily_breakdown: [], top_skus: [], hourly_distribution: {} });
        setCreditData({ current_balance: 0, usage_forecast: {} });
        setPublicTryonSummary({});
      }
    } catch (error) {
      console.error('Analytics fetch error:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (type) => {
    if (!hasProfessionalAccess) {
      toast.error('Export requires Professional plan or higher');
      return;
    }
    
    setExporting(true);
    try {
      const response = type === 'usage' 
        ? await analyticsAPI.exportUsageCSV(selectedPeriod)
        : await analyticsAPI.exportTransactionsCSV(selectedPeriod);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}_analytics_${selectedPeriod}d.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${type === 'usage' ? 'Usage' : 'Transaction'} data exported`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const maxDailyRequests = Math.max(...(usageData?.daily_breakdown?.map(d => (d.tryon || 0) + (d.moments || 0)) || [1]));

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              Analytics Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
              <span className="capitalize">{currentPlan}</span> Plan
              {!hasProfessionalAccess && (
                <button 
                  onClick={() => navigate('/partners/subscription')}
                  className="text-blue-600 hover:text-blue-700 text-xs font-medium flex items-center gap-1"
                >
                  <Crown className="w-3 h-3" /> Upgrade for more insights
                </button>
              )}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm"
              data-testid="period-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60} disabled={!hasGrowthAccess}>Last 60 days {!hasGrowthAccess && '🔒'}</option>
              <option value={90} disabled={!hasGrowthAccess}>Last 90 days {!hasGrowthAccess && '🔒'}</option>
            </select>
            
            <button
              onClick={() => handleExport('usage')}
              disabled={exporting || !hasProfessionalAccess}
              className={`px-3 py-2 border rounded-lg text-sm flex items-center gap-2 transition-colors ${
                hasProfessionalAccess 
                  ? 'border-slate-200 hover:bg-slate-50' 
                  : 'border-slate-100 text-slate-400 cursor-not-allowed'
              }`}
              title={hasProfessionalAccess ? 'Export Usage CSV' : 'Requires Professional plan'}
              data-testid="export-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export
              {!hasProfessionalAccess && <Lock className="w-3 h-3" />}
            </button>
            
            <button
              onClick={fetchData}
              disabled={refreshing}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ==================== TIER 1: BASIC (All Plans) ==================== */}
      
      {/* Basic KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard 
          label="Total API Calls" 
          value={formatNumber(usageData?.summary?.total_requests)}
          icon={BarChart3} 
          color="blue"
          subtitle={`${selectedPeriod} day period`}
        />
        <KPICard 
          label="Credits Used" 
          value={formatNumber(usageData?.summary?.total_credits_used)}
          icon={Zap} 
          color="amber"
          subtitle={`${usageData?.summary?.cache_hit_rate || 0}% cached`}
        />
        <KPICard 
          label="Credits Remaining" 
          value={formatNumber(creditData?.current_balance)}
          icon={CreditCard} 
          color="emerald"
          subtitle={`${creditData?.usage_forecast?.days_remaining || '∞'} days left`}
        />
        <KPICard 
          label="TryOn Calls" 
          value={formatNumber(usageData?.summary?.total_tryon)}
          icon={Shirt} 
          color="purple"
          subtitle={`${formatNumber(usageData?.summary?.total_moments)} Moments`}
        />
      </div>

      {/* Daily Usage Chart - Basic (7 days for Starter, full for others) */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Daily API Usage
        </h3>
        
        <div className="h-64">
          {usageData?.daily_breakdown?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={usageData.daily_breakdown.slice(hasGrowthAccess ? 0 : -7)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="tryon" name="TryOn" stackId="1" stroke="#3b82f6" fill="#93c5fd" />
                <Area type="monotone" dataKey="moments" name="Moments" stackId="1" stroke="#8b5cf6" fill="#c4b5fd" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400">
              <Activity className="w-12 h-12 mb-2 opacity-50" />
              <p className="text-sm">No usage data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Credit Forecast - Basic */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            Credit Usage Forecast
          </h3>
          <p className="text-xs text-slate-400">Based on {selectedPeriod}-day average</p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-slate-50 rounded-xl text-center">
            <p className="text-sm text-slate-500 mb-1">Current Balance</p>
            <p className="text-2xl font-bold text-slate-900">{formatNumber(creditData?.current_balance)}</p>
            <p className="text-xs text-slate-400">credits available</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl text-center">
            <p className="text-sm text-slate-500 mb-1">Daily Avg</p>
            <p className="text-2xl font-bold text-blue-600">{creditData?.usage_forecast?.daily_avg_usage || 0}</p>
            <p className="text-xs text-slate-400">credits/day</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl text-center">
            <p className="text-sm text-slate-500 mb-1">Weekly Projection</p>
            <p className="text-2xl font-bold text-purple-600">{creditData?.usage_forecast?.weekly_usage || 0}</p>
            <p className="text-xs text-slate-400">credits/week</p>
          </div>
          <div className={`p-4 rounded-xl text-center ${
            (creditData?.usage_forecast?.days_remaining || 999) < 7 ? 'bg-red-50' :
            (creditData?.usage_forecast?.days_remaining || 999) < 30 ? 'bg-amber-50' :
            'bg-green-50'
          }`}>
            <p className="text-sm text-slate-500 mb-1">Runway</p>
            <p className={`text-2xl font-bold ${
              (creditData?.usage_forecast?.days_remaining || 999) < 7 ? 'text-red-600' :
              (creditData?.usage_forecast?.days_remaining || 999) < 30 ? 'text-amber-600' :
              'text-green-600'
            }`}>
              {creditData?.usage_forecast?.days_remaining > 365 ? '365+' : creditData?.usage_forecast?.days_remaining || '-'}
              <span className="text-sm font-normal text-slate-500 ml-1">days</span>
            </p>
            <p className="text-xs text-slate-400">at current usage</p>
          </div>
        </div>
        
        {(creditData?.usage_forecast?.days_remaining || 999) < 14 && (
          <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <p className="text-amber-800 text-sm">Low credit balance! Add credits to avoid interruption.</p>
            </div>
            <button
              onClick={() => navigate('/partners/wallet')}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
            >
              Add Credits
            </button>
          </div>
        )}
      </div>

      {/* ==================== TIER 2: ADVANCED (Growth+) ==================== */}
      
      <div className="grid lg:grid-cols-2 gap-6">
        {/* SKU Performance */}
        <AnalyticsSection
          title="SKU Performance"
          icon={Package}
          locked={!hasGrowthAccess}
          requiredPlan="growth"
          currentPlan={currentPlan}
          featureName="SKU Analytics"
        >
          <SKUPerformanceTable skus={usageData?.top_skus} locked={!hasGrowthAccess} />
        </AnalyticsSection>

        {/* Hourly Distribution */}
        <AnalyticsSection
          title="Peak Usage Hours"
          icon={Clock}
          locked={!hasGrowthAccess}
          requiredPlan="growth"
          currentPlan={currentPlan}
          featureName="Hourly Analytics"
        >
          <HourlyHeatmap data={usageData?.hourly_distribution} locked={!hasGrowthAccess} />
        </AnalyticsSection>
      </div>

      {/* Customer & Latency Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard 
          label="Unique Customers" 
          value={formatNumber(usageData?.top_customers?.length || 0)}
          icon={Users} 
          color="indigo"
          subtitle="Tracked via customer_id"
          locked={!hasGrowthAccess}
        />
        <KPICard 
          label="Avg Latency" 
          value={`${usageData?.summary?.avg_latency_ms || 0}ms`}
          icon={Clock} 
          color="slate"
          subtitle="API response time"
          locked={!hasGrowthAccess}
        />
        <KPICard 
          label="Cache Hit Rate" 
          value={`${usageData?.summary?.cache_hit_rate || 0}%`}
          icon={Zap} 
          color="green"
          subtitle="Credits saved from cache"
          locked={!hasGrowthAccess}
        />
        <KPICard 
          label="Moments Usage" 
          value={formatNumber(usageData?.summary?.total_moments)}
          icon={Sparkles} 
          color="rose"
          subtitle="Lifestyle scenes generated"
          locked={!hasGrowthAccess}
        />
      </div>

      {/* Moments Breakdown */}
      <AnalyticsSection
        title="Moments Breakdown"
        icon={Sparkles}
        locked={!hasGrowthAccess}
        requiredPlan="growth"
        currentPlan={currentPlan}
        featureName="Moments Analytics"
        className="max-w-2xl"
      >
        <MomentsBreakdown data={usageData?.moment_breakdown} locked={!hasGrowthAccess} />
      </AnalyticsSection>

      {/* ==================== TIER 3: BUSINESS INSIGHTS (Professional+) ==================== */}
      
      <div className="border-t border-slate-200 pt-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-purple-600" />
          Business Insights
          {!hasProfessionalAccess && (
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center gap-1">
              <Lock className="w-3 h-3" /> Professional+
            </span>
          )}
        </h2>
        
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Conversion Funnel */}
          <AnalyticsSection
            title="Conversion Funnel"
            icon={Activity}
            locked={!hasProfessionalAccess}
            requiredPlan="professional"
            currentPlan={currentPlan}
            featureName="Conversion Analytics"
          >
            <ConversionFunnel data={publicTryonSummary} locked={!hasProfessionalAccess} />
          </AnalyticsSection>

          {/* Stock Alerts / Inventory Intelligence */}
          <AnalyticsSection
            title="SKU Demand Alerts"
            icon={AlertTriangle}
            locked={!hasProfessionalAccess}
            requiredPlan="professional"
            currentPlan={currentPlan}
            featureName="Inventory Intelligence"
          >
            <StockAlerts alerts={inventoryAlerts} locked={!hasProfessionalAccess} />
          </AnalyticsSection>
        </div>
      </div>

      {/* Business KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard 
          label="Buy Click Rate" 
          value={`${publicTryonSummary?.buy_click_rate || 0}%`}
          icon={ShoppingCart} 
          color="green"
          subtitle="Try-on to purchase intent"
          locked={!hasProfessionalAccess}
        />
        <KPICard 
          label="Like Rate" 
          value={`${publicTryonSummary?.like_rate || 0}%`}
          icon={Heart} 
          color="rose"
          subtitle="Customer engagement"
          locked={!hasProfessionalAccess}
        />
        <KPICard 
          label="Cost per Conversion" 
          value={publicTryonSummary?.total_buy_clicks > 0 
            ? `${Math.round((usageData?.summary?.total_credits_used || 0) / publicTryonSummary.total_buy_clicks)} cr`
            : '-'
          }
          icon={Target} 
          color="purple"
          subtitle="Credits per buy click"
          locked={!hasProfessionalAccess}
        />
        <KPICard 
          label="Customers w/ Consent" 
          value={formatNumber(publicTryonSummary?.customers_with_consent || 0)}
          icon={Users} 
          color="indigo"
          subtitle="Marketing eligible"
          locked={!hasProfessionalAccess}
        />
      </div>

      {/* Upgrade CTA for Starter/Growth */}
      {!hasProfessionalAccess && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white"
        >
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Crown className="w-8 h-8" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-lg font-semibold mb-1">Unlock Full Business Insights</h3>
              <p className="text-white/80 text-sm">
                Upgrade to Professional for conversion funnels, inventory intelligence, exportable reports, and more.
              </p>
            </div>
            <button
              onClick={() => navigate('/partners/subscription')}
              className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors flex-shrink-0"
            >
              Upgrade Now
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
